import { Link } from 'react-router-dom';
import { Building2, FileQuestion, Package, TrendingUp, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/dashboard/StatCard';
import { usePlatformDashboard } from '@/hooks/usePlatformDashboard';

const TIER_LABELS: Record<string, string> = {
  FREE: 'Free',
  BASIC: 'Basic',
  PROFESSIONAL: 'Professional',
  ENTERPRISE: 'Enterprise',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  trial: 'bg-blue-100 text-blue-800',
};

function formatNumber(n: number): string {
  return n.toLocaleString('de-DE');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE');
}

export function PlatformDashboard() {
  const { data, isLoading, isError, error } = usePlatformDashboard();

  return (
    <div>
      <h1 className="text-2xl font-bold">Platform Dashboard</h1>
      <p className="mt-2 text-muted-foreground">Plattform-Kennzahlen im Überblick.</p>

      {/* KPI Cards */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Tenants gesamt"
          value={formatNumber(data?.tenants.total ?? 0)}
          icon={Building2}
          description={
            data
              ? `${data.tenants.active} aktiv · ${data.tenants.trial} Trial · ${data.tenants.suspended} gesperrt`
              : undefined
          }
          isLoading={isLoading}
          isError={isError}
        />
        <StatCard
          title="Quizzes gesamt"
          value={formatNumber(data?.quizzes.total ?? 0)}
          icon={FileQuestion}
          description={
            data
              ? `${data.quizzes.published} publiziert · ${data.quizzes.draft} Entwurf`
              : undefined
          }
          isLoading={isLoading}
          isError={isError}
        />
        <StatCard
          title="Produkte gesamt"
          value={formatNumber(data?.products.total ?? 0)}
          icon={Package}
          description="Über alle Tenants"
          isLoading={isLoading}
          isError={isError}
        />
        <StatCard
          title="Quiz-Abschlüsse"
          value={formatNumber(data?.completions.total ?? 0)}
          icon={TrendingUp}
          description={
            data
              ? `${formatNumber(data.completions.last7Days)} letzte 7 Tage · ${formatNumber(data.completions.last30Days)} letzte 30 Tage`
              : undefined
          }
          isLoading={isLoading}
          isError={isError}
        />
      </div>

      {/* Tier Distribution */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tier-Verteilung</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-16 flex-1 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span>Fehler beim Laden der Tier-Daten</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(TIER_LABELS).map(([key, label]) => (
                  <div key={key} className="rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold">
                      {formatNumber(data?.tenants.byTier[key] ?? 0)}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Tenants */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Letzte Tenants</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span>{error instanceof Error ? error.message : 'Fehler beim Laden'}</span>
              </div>
            ) : !data?.recentTenants.length ? (
              <p className="text-sm text-muted-foreground">Keine Tenants vorhanden.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 font-medium">Name</th>
                      <th className="pb-2 font-medium">Tier</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Erstellt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentTenants.map((tenant) => (
                      <tr key={tenant.id} className="border-b last:border-0">
                        <td className="py-2">
                          <Link
                            to={`/platform/tenants/${tenant.id}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {tenant.name}
                          </Link>
                          <div className="text-xs text-muted-foreground">{tenant.slug}</div>
                        </td>
                        <td className="py-2">{TIER_LABELS[tenant.subscriptionTier] ?? tenant.subscriptionTier}</td>
                        <td className="py-2">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[tenant.status] ?? 'bg-muted text-muted-foreground'}`}
                          >
                            {tenant.status}
                          </span>
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {formatDate(tenant.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
